// client/src/pages/marketing/AlgemeneVoorwaardenPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/marketing/Navbar';
import Hero from '../../components/marketing/Hero';
import Footer from '../../components/marketing/Footer';

const AlgemeneVoorwaardenPage = () => {
  return (
    <div className="min-h-screen bg-cream-100 text-charcoal-800 font-['Lexend_Deca'] text-base leading-relaxed">
      <Navbar />

      <Hero
        title="Algemene Voorwaarden"
        subtitle="Versie 1.0 | Datum inwerkingtreding: 1 maart 2026"
        height="normal"
      />

      <main className="mx-auto max-w-3xl py-20 px-5">

        {/* Artikel 1 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 1 - Definities</h2>
          <p className="mb-4">
            In deze algemene voorwaarden worden de volgende begrippen met de navolgende betekenis gebruikt,
            tenzij uitdrukkelijk anders is aangegeven:
          </p>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              <strong>Leren Motiveren:</strong> de eenmanszaak Leren Motiveren, handelend onder de naam LerenMotiveren,
              ingeschreven bij de Kamer van Koophandel onder nummer 99583755, BTW-nummer NL869050199B01,
              gevestigd te Oudwijkerdwarsstraat 73, 3581LB Utrecht, hierna ook te noemen "wij" of "ons";
            </li>
            <li>
              <strong>Platform:</strong> de online leeromgeving van Leren Motiveren, toegankelijk via
              www.lerenmotiveren.nl, waarop de Cursussen worden aangeboden;
            </li>
            <li>
              <strong>Gebruiker:</strong> iedere natuurlijke persoon of rechtspersoon die gebruik maakt van het
              Platform, hierna ook te noemen "u";
            </li>
            <li>
              <strong>Cursus:</strong> een door Leren Motiveren aangeboden online training in motiverende
              gespreksvoering, bestaande uit modules, oefeningen en AI-gestuurde conversatiesimulaties;
            </li>
            <li>
              <strong>Account:</strong> de persoonlijke toegang van de Gebruiker tot het Platform, beschermd
              door inloggegevens;
            </li>
            <li>
              <strong>AI-technologie:</strong> de door Leren Motiveren ingezette kunstmatige intelligentie (o.a.
              Claude API van Anthropic) voor het genereren van realistische conversatiesimulaties en feedback;
            </li>
            <li>
              <strong>Geaccrediteerde Punten:</strong> punten die door een erkende accrediterende instantie worden
              toegekend aan een scholingsactiviteit en die kunnen worden geregistreerd in een relevant beroeps- of
              kwaliteitsregister;
            </li>
            <li>
              <strong>Overeenkomst:</strong> de overeenkomst tussen Leren Motiveren en de Gebruiker betreffende
              toegang tot en gebruik van een Cursus;
            </li>
            <li>
              <strong>Fair use:</strong> redelijk en normaal gebruik van het Platform en de AI-technologie conform
              de educatieve doeleinden van de Cursus;
            </li>
            <li>
              <strong>Consument:</strong> een Gebruiker die handelt voor doeleinden buiten zijn bedrijfs- of
              beroepsactiviteit;
            </li>
            <li>
              <strong>Zakelijke gebruiker:</strong> een Gebruiker die handelt in de uitoefening van een beroep of bedrijf.
            </li>
          </ol>
        </section>

        {/* Artikel 2 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 2 - Toepasselijkheid</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Deze algemene voorwaarden zijn van toepassing op alle aanbiedingen, overeenkomsten en dienstverlening
              van Leren Motiveren.
            </li>
            <li>
              Door een Account aan te maken of een Cursus aan te schaffen, accepteert u deze algemene voorwaarden.
            </li>
            <li>
              Afwijkingen van deze voorwaarden zijn slechts geldig indien deze uitdrukkelijk schriftelijk zijn overeengekomen.
            </li>
            <li>
              Indien een of meer bepalingen van deze voorwaarden nietig of vernietigbaar blijken te zijn, blijven de
              overige bepalingen volledig van kracht.
            </li>
            <li>
              Deze voorwaarden kunnen periodiek worden aangepast. De actuele versie is te allen tijde beschikbaar op het Platform.
            </li>
          </ol>
        </section>

        {/* Artikel 3 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 3 - Totstandkoming van de overeenkomst</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              De overeenkomst komt tot stand op het moment dat u een Cursus aanschaft en de betaling succesvol is voltooid.
            </li>
            <li>
              Voorafgaand aan de aankoop ontvangt u duidelijke informatie over:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>De inhoud en duur van de Cursus</li>
                <li>De prijs inclusief BTW</li>
                <li>De toegangsduur (1 jaar)</li>
                <li>De fair use policy</li>
                <li>Het herroepingsrecht (indien van toepassing)</li>
              </ul>
            </li>
            <li>
              Bij aankoop van een Cursus verklaart u:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>De algemene voorwaarden te hebben gelezen en te accepteren</li>
                <li>Indien consument: uitdrukkelijk in te stemmen met directe levering en daarmee afstand te doen van het herroepingsrecht</li>
                <li>Dat de verstrekte gegevens juist en volledig zijn</li>
              </ul>
            </li>
            <li>
              Na succesvolle betaling ontvangt u per e-mail een bevestiging met toegang tot uw Account.
            </li>
          </ol>
        </section>

        {/* Artikel 4 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 4 - Account en toegang</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Voor toegang tot een Cursus is een persoonlijk Account vereist.
            </li>
            <li>
              U bent verantwoordelijk voor:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Het geheimhouden van uw inloggegevens</li>
                <li>Alle activiteiten die via uw Account plaatsvinden</li>
                <li>Het onmiddellijk melden van (vermoeden van) ongeautoriseerd gebruik</li>
              </ul>
            </li>
            <li>
              Het Account is strikt persoonlijk en mag niet worden gedeeld, overgedragen of aan derden ter beschikking worden gesteld.
            </li>
            <li>
              Toegang tot een aangekochte Cursus is geldig voor 1 jaar vanaf de datum van aankoop, tenzij de toegang
              eerder wordt beëindigd conform deze voorwaarden.
            </li>
            <li>
              Na afloop van de toegangsperiode vervalt de toegang automatisch. Verlenging is mogelijk door een nieuwe aankoop.
            </li>
            <li>
              Wij behouden ons het recht voor om Accounts te blokkeren of te verwijderen bij:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Schending van deze voorwaarden</li>
                <li>Misbruik van het Platform of de AI-technologie</li>
                <li>Frauduleus of onrechtmatig handelen</li>
                <li>Niet-betaling</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 5 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 5 - Betaling en prijzen</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Alle prijzen op het Platform zijn in euro's en inclusief BTW, tenzij uitdrukkelijk anders vermeld.
            </li>
            <li>
              Betaling geschiedt via de op het Platform aangeboden betaalmethoden (waaronder iDEAL via Mollie).
            </li>
            <li>
              Toegang tot de Cursus wordt verleend na succesvolle ontvangst van de betaling.
            </li>
            <li>
              Bij gebreke van tijdige betaling of bij terugboeking van een betaling kan de toegang worden opgeschort
              totdat de betaling alsnog is ontvangen.
            </li>
            <li>
              Eventuele transactiekosten komen voor rekening van de Gebruiker, tenzij anders overeengekomen.
            </li>
            <li>
              Leren Motiveren behoudt zich het recht voor om prijzen te wijzigen. Prijswijzigingen gelden niet voor
              reeds gesloten overeenkomsten.
            </li>
          </ol>
        </section>

        {/* Artikel 6 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 6 - Gebruiksrecht en intellectueel eigendom</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Alle intellectuele eigendomsrechten op het Platform, de Cursussen, de content, het ontwerp, de structuur,
              de software en de onderliggende technologie berusten bij Leren Motiveren of haar licentiegevers.
            </li>
            <li>
              U verkrijgt een niet-exclusief, niet-overdraagbaar, persoonlijk gebruiksrecht voor de duur van uw toegang
              tot de aangekochte Cursus.
            </li>
            <li>
              Het is u niet toegestaan om:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>De Cursusinhoud te kopiëren, verveelvoudigen of verspreiden</li>
                <li>De Cursusinhoud commercieel te exploiteren</li>
                <li>Het Platform of de technologie te reverse-engineeren, decompileren of anderszins te analyseren</li>
                <li>Inloggegevens te delen met derden</li>
                <li>Geautomatiseerde systemen (bots, scripts) te gebruiken om het Platform te benaderen</li>
                <li>Beveiligingsmaatregelen te omzeilen of het Platform te misbruiken</li>
              </ul>
            </li>
            <li>
              U mag voor persoonlijke studie-doeleinden aantekeningen maken, maar publicatie of commercieel gebruik
              hiervan is niet toegestaan zonder voorafgaande schriftelijke toestemming.
            </li>
          </ol>
        </section>

        {/* Artikel 7 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 7 - AI-technologie en fair use policy</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Leren Motiveren maakt gebruik van geavanceerde AI-technologie (waaronder de Claude API van Anthropic)
              voor het genereren van realistische conversatiesimulaties en gepersonaliseerde feedback.
            </li>
            <li>
              <strong>Aard van AI-technologie:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>AI-responses worden in real-time gegenereerd op basis van machine learning modellen</li>
                <li>Hoewel de AI zorgvuldig is getraind en gemonitord, kunnen responses incidenteel onvolkomenheden bevatten</li>
                <li>De AI-technologie dient uitsluitend als trainingsinstrument en niet als vervanging van professionele supervisie of als medisch advies</li>
              </ul>
            </li>
            <li>
              <strong>Fair use policy:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Normaal gebruik van de Cursus (het voltooien van modules, redelijke herhaling voor leerdoeleinden) is zonder beperking toegestaan</li>
                <li>Het Platform is ontworpen om voldoende AI-capaciteit te bieden voor het succesvol afronden van de Cursus</li>
                <li>Er geldt geen hard token-limiet voor normaal educatief gebruik</li>
              </ul>
            </li>
            <li>
              <strong>Verboden gebruik / Misbruik:</strong> Als misbruik wordt beschouwd:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Het gebruik van geautomatiseerde scripts, bots of andere tools om excessieve AI-requests te genereren</li>
                <li>Het bewust overbelasten van de AI-systemen zonder educatief doel</li>
                <li>Het proberen te misbruiken, hacken of anderszins manipuleren van de AI-technologie</li>
                <li>Elk ander gebruik dat de normale werking van het Platform belemmert of onevenredige kosten veroorzaakt</li>
              </ul>
            </li>
            <li>
              <strong>Monitoring en handhaving:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Leren Motiveren monitort het gebruik van AI-technologie om de kwaliteit te waarborgen en misbruik te voorkomen</li>
                <li>Bij geconstateerd misbruik behouden wij ons het recht voor om:
                  <ul className="list-disc list-inside ml-8 mt-1 space-y-1">
                    <li>U te waarschuwen en te verzoeken het gebruik te normaliseren</li>
                    <li>Uw toegang tijdelijk op te schorten</li>
                    <li>Bij voortgezet of ernstig misbruik: uw Account definitief te blokkeren</li>
                    <li>Meerkosten die voortvloeien uit misbruik bij u in rekening te brengen</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              <strong>Rapportage van problemen:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Mocht u technische problemen of ongewenst gedrag van de AI waarnemen, dan verzoeken wij u dit te melden via <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a></li>
                <li>Wij onderzoeken alle meldingen en nemen waar nodig verbetermaatregelen</li>
              </ul>
            </li>
            <li>
              <strong>Gebruik van third-party services:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Voor de AI-functionaliteit maken wij gebruik van diensten van derde partijen (o.a. Anthropic/Claude API)</li>
                <li>Deze diensten hebben hun eigen gebruiksvoorwaarden en beschikbaarheid</li>
                <li>Leren Motiveren is niet aansprakelijk voor uitval of wijzigingen in deze externe diensten, behoudens artikel 12</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 8 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 8 - Certificering en Geaccrediteerde Punten</h2>

          <h3 className="text-lg font-semibold text-purple-700 mb-2 mt-6">8.1 Certificering</h3>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              De Cursus kan – indien van toepassing – geaccrediteerd zijn door een erkende accrediterende instantie of beroepsregister.
            </li>
            <li>
              Bij succesvolle afronding van de Cursus ontvangt de deelnemer automatisch een certificaat van deelname.
            </li>
          </ol>

          <h3 className="text-lg font-semibold text-purple-700 mb-2 mt-6">8.2 Voorwaarden voor het certificaat</h3>
          <p className="mb-2">Het certificaat wordt verstrekt indien:</p>
          <ul className="list-disc list-inside ml-8 space-y-1 mb-3">
            <li>alle verplichte modules van de Cursus volledig zijn doorlopen;</li>
            <li>aan eventuele aanvullende afrondingsvereisten is voldaan.</li>
          </ul>
          <p>Het certificaat wordt automatisch gegenereerd en per e-mail aan de deelnemer toegezonden.</p>

          <h3 className="text-lg font-semibold text-purple-700 mb-2 mt-6">8.3 Geaccrediteerde Punten</h3>
          <p className="mb-2">Indien op de Cursus Geaccrediteerde Punten van toepassing zijn, gelden de volgende bepalingen:</p>
          <ul className="list-disc list-inside ml-8 space-y-1">
            <li>Leren Motiveren draagt behaalde punten periodiek over aan de betreffende accrediterende instantie of registreert deze via het daarvoor geldende registratiesysteem, indien dit onderdeel is van de accreditatieafspraken.</li>
            <li>De deelnemer blijft te allen tijde zelf verantwoordelijk voor het controleren of de punten correct zijn verwerkt in het voor hem of haar geldende beroeps- of kwaliteitsregister.</li>
            <li>Aan eventuele voorwaarden of eisen van het betreffende beroepsregister kunnen geen rechten worden ontleend jegens Leren Motiveren.</li>
            <li>Voor vragen over registratie van Geaccrediteerde Punten kan contact worden opgenomen via <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a>.</li>
          </ul>

          <h3 className="text-lg font-semibold text-purple-700 mb-2 mt-6">8.4 Professionele verantwoordelijkheid</h3>
          <p className="mb-2">Het certificaat en eventuele Geaccrediteerde Punten bevestigen uitsluitend dat de deelnemer de Cursus heeft gevolgd en afgerond.</p>
          <p className="mb-2">De deelnemer blijft zelf verantwoordelijk voor:</p>
          <ul className="list-disc list-inside ml-8 space-y-1 mb-3">
            <li>het onderhouden van professionele competentie;</li>
            <li>de correcte toepassing van de geleerde vaardigheden in de praktijk;</li>
            <li>naleving van wet- en regelgeving en beroepsspecifieke richtlijnen.</li>
          </ul>
          <p>De Cursus is een hulpmiddel voor professionele ontwikkeling en vervangt geen formele opleiding, supervisie, registratie-eisen of andere wettelijke vereisten voor beroepsuitoefening.</p>

          <h3 className="text-lg font-semibold text-purple-700 mb-2 mt-6">8.5 Geen medisch advies</h3>
          <p>De Cursus biedt scholing in gespreksvaardigheden en bevat geen medisch, paramedisch of therapeutisch advies. Voor medische of inhoudelijke vakvragen dient de deelnemer zijn of haar eigen professionele oordeel en geldende richtlijnen te volgen.</p>
        </section>

        {/* Artikel 9 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 9 - Verwerking van persoonsgegevens</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Leren Motiveren verwerkt persoonsgegevens in overeenstemming met de Algemene Verordening Gegevensbescherming
              (AVG/GDPR) en andere toepasselijke privacywetgeving.
            </li>
            <li>
              Voor uitgebreide informatie over welke gegevens wij verzamelen, hoe wij deze gebruiken en wat uw rechten zijn,
              verwijzen wij naar ons Privacy Statement op <Link to="/privacy" className="text-purple-600 hover:text-purple-800">www.lerenmotiveren.nl/privacy</Link>.
            </li>
            <li>
              In het kort:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Wij verzamelen: accountgegevens, voortgangsdata, conversatielogboeken en evaluatieresultaten</li>
                <li>Doel: het leveren van de dienst, verbetering van de Cursus, support en registratie van Geaccrediteerde Punten</li>
                <li>Bewaartermijn: tijdens uw actieve Account + 12 maanden, tenzij wettelijke bewaarplicht anders vereist</li>
                <li>Verwijdering: binnen 30 dagen na uw verzoek tot accountverwijdering</li>
                <li>Gegevensopslag: binnen de EU (GDPR-compliant)</li>
              </ul>
            </li>
            <li>
              Conversatiegegevens en voortgangsdata worden uitsluitend gebruikt voor het leveren van de dienst en kunnen
              in <strong>geanonimiseerde vorm</strong> worden gebruikt voor kwaliteitsverbetering van het Platform.
            </li>
            <li>
              Uw persoonsgegevens worden niet verkocht aan of gedeeld met derden voor commerciële doeleinden.
            </li>
            <li>
              U heeft recht op inzage, correctie, verwijdering, beperking, bezwaar en dataportabiliteit. Voor uitoefening
              van deze rechten kunt u contact opnemen via <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a>.
            </li>
          </ol>
        </section>

        {/* Artikel 10 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 10 - Herroepingsrecht (voor consumenten)</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Indien u als consument een Cursus aanschaft, heeft u op grond van de wet een herroepingsrecht van 14 dagen na aankoop.
            </li>
            <li>
              <strong>Uitzondering - Directe levering digitale inhoud:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Aangezien de Cursus een digitale dienst betreft waarbij u onmiddellijk toegang verkrijgt, vervalt uw herroepingsrecht zodra u:
                  <ul className="list-disc list-inside ml-8 mt-1 space-y-1">
                    <li>Uitdrukkelijk heeft ingestemd met directe levering, én</li>
                    <li>Heeft erkend dat u daarmee uw herroepingsrecht verliest, én</li>
                    <li>De Cursus bent gestart (inloggen en toegang tot content)</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              Bij de aankoop wordt u uitdrukkelijk gevraagd om in te stemmen met deze voorwaarden via een checkbox.
            </li>
            <li>
              Heeft u de Cursus nog niet gestart binnen 14 dagen na aankoop? Dan kunt u binnen die termijn kosteloos herroepen
              door een duidelijke verklaring te sturen naar <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a>.
            </li>
            <li>
              <strong>Geen herroepingsrecht voor zakelijke gebruikers:</strong> Indien u zakelijk een Cursus aanschaft, is het
              herroepingsrecht niet van toepassing.
            </li>
          </ol>
        </section>

        {/* Artikel 11 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 11 - Restitutie</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Na aanvang van de Cursus (na het vervallen van het herroepingsrecht) worden geen restituties verstrekt.
            </li>
            <li>
              <strong>Uitzondering - annulering door Leren Motiveren:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Indien wij een Cursus annuleren of niet kunnen leveren om redenen die aan ons te wijten zijn, ontvangt u een volledige restitutie van het aankoopbedrag</li>
                <li>Restitutie vindt plaats binnen 14 dagen op dezelfde wijze als de oorspronkelijke betaling</li>
              </ul>
            </li>
            <li>
              <strong>Technische problemen:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Bij tijdelijke technische problemen waardoor de Cursus (gedeeltelijk) niet toegankelijk is, kan uw toegangsperiode worden verlengd met de duur van de storing</li>
                <li>Bij structurele problemen die de toegang langdurig belemmeren, kunnen wij in overleg een passende oplossing bieden (verlenging of restitutie)</li>
              </ul>
            </li>
            <li>
              <strong>Restitutie vindt niet plaats bij:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Schending van deze voorwaarden door de Gebruiker</li>
                <li>Blokkering wegens misbruik</li>
                <li>Ontevredenheid over het behaalde leerresultaat</li>
                <li>Technische problemen aan de zijde van de Gebruiker (internetverbinding, apparatuur)</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 12 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 12 - Aansprakelijkheid en garanties</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              <strong>Inspanningsverplichting:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Leren Motiveren spant zich naar beste vermogen in om het Platform en de Cursussen van hoge kwaliteit en actueel te houden</li>
                <li>Wij garanderen echter geen foutloze of ononderbroken beschikbaarheid</li>
              </ul>
            </li>
            <li>
              <strong>Beperking aansprakelijkheid:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>De aansprakelijkheid van Leren Motiveren voor directe schade is beperkt tot maximaal het bedrag dat u voor de betreffende Cursus heeft betaald</li>
                <li>Aansprakelijkheid voor indirecte schade (waaronder gevolgschade, gederfde winst, gemiste besparingen, verlies van gegevens of schade door bedrijfsstagnatie) is uitgesloten</li>
              </ul>
            </li>
            <li>
              <strong>Uitgesloten aansprakelijkheid:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Schade door onjuist of onzorgvuldig gebruik van het Platform door de Gebruiker</li>
                <li>Schade door het niet opvolgen van instructies of waarschuwingen</li>
                <li>Schade door technische problemen aan de zijde van de Gebruiker</li>
                <li>Schade door uitval van third-party diensten (zoals de Claude API) buiten onze invloedssfeer</li>
                <li>Schade voortvloeiend uit AI-gegenereerde content (de AI dient als trainingsinstrument, geen professioneel advies)</li>
              </ul>
            </li>
            <li>
              <strong>Wettelijke uitzonderingen:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Bovengenoemde aansprakelijkheidsbeperkingen gelden niet indien schade het gevolg is van opzet of grove schuld van Leren Motiveren</li>
                <li>Aansprakelijkheid voor letsel- of gezondheidsschade kan niet worden uitgesloten</li>
              </ul>
            </li>
            <li>
              <strong>Klachtplicht:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>U dient gebreken of klachten binnen bekwame tijd (uiterlijk 14 dagen na ontdekking) te melden via <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a></li>
                <li>Niet tijdig melden kan leiden tot verlies van rechten</li>
              </ul>
            </li>
            <li>
              <strong>Vrijwaring:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>U vrijwaart Leren Motiveren voor claims van derden die voortvloeien uit uw gebruik van het Platform in strijd met deze voorwaarden of toepasselijke wetgeving</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 13 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 13 - Support en beschikbaarheid</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              <strong>Support:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Leren Motiveren biedt support via e-mail: <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a></li>
                <li>Wij streven ernaar om binnen 2 werkdagen (maandag t/m vrijdag, kantooruren 9:00-17:00 uur) te reageren op supportverzoeken</li>
                <li>Support is beschikbaar in het Nederlands</li>
              </ul>
            </li>
            <li>
              <strong>Beschikbaarheid Platform:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Wij streven naar een zo hoog mogelijke beschikbaarheid van het Platform</li>
                <li>Er wordt geen gegarandeerde uptime geboden (best-effort basis)</li>
                <li>Gepland onderhoud wordt waar mogelijk van tevoren aangekondigd</li>
              </ul>
            </li>
            <li>
              <strong>Technische vereisten:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Voor gebruik van het Platform heeft u een stabiele internetverbinding en een moderne webbrowser nodig</li>
                <li>Technische specificaties zijn vermeld op het Platform</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 14 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 14 - Overmacht</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Leren Motiveren is niet aansprakelijk voor tekortkoming in de nakoming van verplichtingen indien dit het
              gevolg is van overmacht.
            </li>
            <li>
              Onder overmacht wordt verstaan: elke van de wil van Leren Motiveren onafhankelijke omstandigheid die nakoming
              geheel of gedeeltelijk verhindert, waaronder:
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Storingen in internet, dataverkeer of telecommunicatie</li>
                <li>Uitval van third-party diensten (zoals de Claude API, hosting providers)</li>
                <li>Stroomstoringen</li>
                <li>Natuurrampen, pandemieën</li>
                <li>Overheidsmaatregelen</li>
                <li>Stakingen</li>
                <li>Distributiestoringen</li>
              </ul>
            </li>
            <li>
              Bij tijdelijke overmacht wordt de verplichting opgeschort. Bij blijvende of langdurige overmacht (langer dan
              2 maanden) hebben beide partijen het recht de overeenkomst te ontbinden, zonder verplichting tot schadevergoeding.
            </li>
          </ol>
        </section>

        {/* Artikel 15 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 15 - Wijzigingen</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              <strong>Wijziging van deze voorwaarden:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Leren Motiveren behoudt zich het recht voor deze algemene voorwaarden te wijzigen</li>
                <li>Wijzigingen worden minimaal 30 dagen voor inwerkingtreding aangekondigd via e-mail en/of het Platform</li>
                <li>Bij materiële wijzigingen heeft u het recht de overeenkomst te beëindigen</li>
              </ul>
            </li>
            <li>
              <strong>Wijziging van de Cursus:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Wij behouden ons het recht voor om de inhoud, structuur en functionaliteit van de Cursus te wijzigen om de kwaliteit en actualiteit te waarborgen</li>
                <li>Bestaande gebruikers krijgen automatisch en kosteloos toegang tot verbeterde versies</li>
                <li>Kernfunctionaliteit en educatieve waarde blijven gewaarborgd</li>
              </ul>
            </li>
            <li>
              <strong>Wijziging van prijzen:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Prijswijzigingen zijn mogelijk maar gelden niet voor reeds gesloten overeenkomsten</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 16 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 16 - Beëindiging</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              <strong>Beëindiging door gebruiker:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>U kunt uw Account op elk moment beëindigen door contact op te nemen met <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a></li>
                <li>Bij beëindiging vervalt uw toegang tot de Cursus met onmiddellijke ingang</li>
                <li>Er vindt geen restitutie plaats (tenzij artikel 11 van toepassing is)</li>
              </ul>
            </li>
            <li>
              <strong>Beëindiging door Leren Motiveren:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Wij kunnen de overeenkomst en uw toegang met onmiddellijke ingang beëindigen bij:
                  <ul className="list-disc list-inside ml-8 mt-1 space-y-1">
                    <li>Schending van deze voorwaarden</li>
                    <li>Misbruik van het Platform of de AI-technologie</li>
                    <li>Wanbetaling</li>
                    <li>Frauduleus of onrechtmatig handelen</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              <strong>Gevolgen van beëindiging:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Uw Account en toegang worden verwijderd</li>
                <li>Persoonsgegevens worden verwerkt conform artikel 9 en het Privacy Statement</li>
                <li>Bepalingen die naar hun aard bedoeld zijn voort te duren (waaronder intellectueel eigendom, aansprakelijkheid, toepasselijk recht) blijven van kracht</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 17 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 17 - Klachten en geschillen</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              <strong>Klachtenprocedure:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Indien u niet tevreden bent over onze dienstverlening, verzoeken wij u eerst contact met ons op te nemen via <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a></li>
                <li>Wij streven ernaar klachten binnen 14 dagen te behandelen</li>
                <li>Kunt u er samen met ons niet uitkomen? Dan kunt u gebruikmaken van de hieronder vermelde geschillenregeling</li>
              </ul>
            </li>
            <li>
              <strong>Geschillenregeling voor consumenten:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Consumenten kunnen een geschil voorleggen aan de Geschillencommissie E-learning/Thuiswinkel Waarborg via <a href="https://www.degeschillencommissie.nl" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800">www.degeschillencommissie.nl</a></li>
                <li>De uitspraak van de geschillencommissie is bindend</li>
              </ul>
            </li>
            <li>
              <strong>Geschillenregeling voor zakelijke gebruikers:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Geschillen die niet in onderling overleg kunnen worden opgelost, worden voorgelegd aan de bevoegde rechter zoals bepaald in artikel 18</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Artikel 18 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 18 - Toepasselijk recht en bevoegde rechter</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              Op alle overeenkomsten tussen Leren Motiveren en de Gebruiker is Nederlands recht van toepassing.
            </li>
            <li>
              <strong>Voor consumenten:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Geschillen worden bij voorkeur opgelost via de geschillencommissie (artikel 17)</li>
                <li>Indien geen oplossing via de geschillencommissie mogelijk is, is de bevoegde rechter de rechtbank van de woonplaats van de consument of de rechtbank van de vestigingsplaats van Leren Motiveren</li>
              </ul>
            </li>
            <li>
              <strong>Voor zakelijke gebruikers:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Alle geschillen worden voorgelegd aan de bevoegde rechter in het arrondissement van de vestigingsplaats van Leren Motiveren (rechtbank Midden-Nederland)</li>
              </ul>
            </li>
            <li>
              Het Weens Koopverdrag is niet van toepassing.
            </li>
          </ol>
        </section>

        {/* Artikel 19 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Artikel 19 - Slotbepalingen</h2>
          <ol className="list-decimal list-inside ml-4 space-y-3">
            <li>
              <strong>Volledigheid:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Deze algemene voorwaarden vormen samen met het Privacy Statement de volledige overeenkomst tussen partijen</li>
                <li>Eerdere afspraken, toezeggingen of correspondentie die niet in deze voorwaarden zijn opgenomen, zijn niet bindend</li>
              </ul>
            </li>
            <li>
              <strong>Overdraagbaarheid:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Leren Motiveren mag rechten en verplichtingen uit deze overeenkomst overdragen aan derden</li>
                <li>De Gebruiker mag zijn rechten niet overdragen zonder voorafgaande schriftelijke toestemming</li>
              </ul>
            </li>
            <li>
              <strong>Deelbaarheid:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Indien een bepaling van deze voorwaarden nietig of onverbindend blijkt, blijven de overige bepalingen onverminderd van kracht</li>
                <li>De ongeldige bepaling wordt vervangen door een geldige bepaling die de bedoeling zo dicht mogelijk benadert</li>
              </ul>
            </li>
            <li>
              <strong>Geen afstand van recht:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Het feit dat Leren Motiveren een recht niet uitoefent of niet direct afdwingt, betekent niet dat afstand wordt gedaan van dat recht</li>
              </ul>
            </li>
            <li>
              <strong>Communicatie:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Officiële communicatie geschiedt per e-mail naar het door u opgegeven e-mailadres</li>
                <li>Het is uw verantwoordelijkheid om een geldig e-mailadres te verstrekken en te onderhouden</li>
              </ul>
            </li>
            <li>
              <strong>Taalversie:</strong>
              <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                <li>Deze voorwaarden zijn opgesteld in het Nederlands</li>
                <li>Bij eventuele vertalingen prevaleert de Nederlandse versie</li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Contactgegevens */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Contactgegevens</h2>
          <address className="not-italic p-4 bg-gray-50 rounded-lg">
            <strong>Leren Motiveren</strong><br />
            Oudwijkerdwarsstraat 73<br />
            3581LB Utrecht<br /><br />
            KVK-nummer: 99583755<br />
            BTW-nummer: NL869050199B01<br /><br />
            E-mail: <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a><br />
            Website: <a href="https://www.lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">www.lerenmotiveren.nl</a>
          </address>
        </section>

        {/* Marketing communicatie */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Marketing communicatie</h2>
          <p>
            Voor marketingcommunicatie geldt een opt-in systeem. U kunt zich te allen tijde afmelden voor nieuwsbrieven
            en promotionele e-mails via de afmeldlink in de e-mail of door contact op te nemen met{' '}
            <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a>.
            Transactionele e-mails (zoals toegangsgegevens, certificaten en registratiebevestigingen) blijven u bereiken.
          </p>
        </section>

        {/* Footer info */}
        <section className="mt-16 pt-8 border-t border-gray-300">
          <p className="text-sm text-gray-600 italic">
            Laatste wijziging: 1 maart 2026<br />
            Versie: 1.0
          </p>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default AlgemeneVoorwaardenPage;
